import { useEffect, useState } from 'react';
import posthog from 'posthog-js';

/**
 * Which paywall offer the web funnel's Pro card makes, decided by a PostHog
 * multivariate feature flag so the experiment can be run from the PostHog UI
 * without a deploy. Same shape and rules as the welcome experiment
 * (useWelcomeVariant); the arms themselves are priced by the backend
 * (core/config.py FUNNEL_OFFER_VARIANTS), the page only names the arm.
 *
 *   control — $7.99 for the first month
 *   lower   — $4.99 for the first month
 *   pwyw    — pay what you want: the parent names the first month's price
 *
 * The first year is the same in every arm. The flag in PostHog: key
 * `funnel-paywall-offer`, variants named exactly as above. Anything else the
 * flag returns (off, unknown variant, PostHog unreachable) is control, so
 * the paywall can never break on the flag's account; and an arm the backend
 * reports as unconfigured is shown and reported as control too
 * (FunnelCheckout.servedOfferVariant).
 *
 * Pinned in sessionStorage once decided, for the same reasons as the
 * welcome arm: the Google round trip reloads the page and sign-in can
 * re-bucket the visitor, and a parent must never see the price change under
 * them. There is no URL override: the flag (or the pinned session value) is
 * the only way into an arm, so a shared link can never pick a price.
 */

export const OFFER_FLAG_KEY = 'funnel-paywall-offer';
export const OFFER_VARIANTS = ['control', 'lower', 'pwyw'];
export const OFFER_VARIANT_KEY = 'rc_funnel_offer_variant';
export const OFFER_FLAG_TIMEOUT_MS = 1000;

const asVariant = (value) => (OFFER_VARIANTS.includes(value) ? value : null);

const readPinned = () => {
  try {
    return asVariant(sessionStorage.getItem(OFFER_VARIANT_KEY));
  } catch (_) {
    return null;
  }
};

const pin = (variant) => {
  try {
    sessionStorage.setItem(OFFER_VARIANT_KEY, variant);
  } catch (_) {
    // not pinned across a reload; the flag decides again
  }
};

// Reading the flag is what has posthog-js emit the `$feature_flag_called`
// exposure the experiment is measured on
const readFlag = () => {
  try {
    return asVariant(posthog.getFeatureFlag(OFFER_FLAG_KEY));
  } catch (_) {
    return null;
  }
};

const flagsReady = () => {
  try {
    return !!posthog.featureFlags?.hasLoadedFlags;
  } catch (_) {
    return false;
  }
};

/**
 * @returns {string|null} The arm, or null for up to a second while PostHog's
 *   first flag response is awaited (callers show control meanwhile; the
 *   paywall is many screens in, so in practice the arm is known at mount).
 */
export const useOfferVariant = () => {
  const [variant, setVariant] = useState(() => {
    const pinned = readPinned();
    if (pinned) return pinned;
    if (!posthog.__loaded) {
      pin('control');
      return 'control';
    }
    const now = readFlag();
    if (now) {
      pin(now);
      return now;
    }
    if (flagsReady()) {
      pin('control');
      return 'control';
    }
    return null;
  });

  useEffect(() => {
    if (variant) return undefined;
    let settled = false;
    const settle = (next) => {
      if (settled) return;
      settled = true;
      pin(next);
      setVariant(next);
    };
    let unsubscribe = () => {};
    try {
      unsubscribe = posthog.onFeatureFlags(() => settle(readFlag() || 'control')) || unsubscribe;
    } catch (_) {
      settle('control');
    }
    const timer = setTimeout(() => settle('control'), OFFER_FLAG_TIMEOUT_MS);
    return () => {
      clearTimeout(timer);
      try {
        unsubscribe();
      } catch (_) {
        // ignore
      }
    };
  }, [variant]);

  return variant;
};

export default useOfferVariant;
